<template>
  <h-f-fields
    :key="fieldKey"
    v-model="selected"
    :label="label"
    :input-type="inputType"
    :model-select="choices"
    :multi-select="choices"
    :taggable="taggable"
    :reduce="false"
    :submitted="submitted"
    :invalid="invalid"
    :required="required"
    :read-only="readOnly"
    @searchchange="searchRessource"
  ></h-f-fields>
</template>

<script>
import { APIRequest } from '@/node-sdk'

export default {
  name: 'model-taggable',

  props: {
    // eslint-disable-next-line vue/require-default-prop
    value: [String, Array, Object],
    label: {
      type: String,
      default: '',
    },
    taggable: {
      type: Boolean,
      default: false,
    },
    inputType: {
      type: String,
      default: 'model-select',
    },
    baseUrl: {
      type: String,
      required: true,
    },
    ressource: {
      type: String,
      required: true,
    },
    submitted: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    textChoices: {
      type: Array,
      default: () => [],
    },
    valueChoice: {
      type: String,
      default: 'id',
    },
    additionalRequest: {
      type: String,
      default: '',
    },
    additionalFields: {
      type: String,
      default: '',
    },
    limitTo: {
      type: Array,
      default: () => [],
    },
    init: {
      type: Boolean,
      default: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selected: null,
      choices: [],
      fieldKey: 0,
    }
  },

  watch: {
    selected() {
      if (this.inputType === 'model-select') {
        if (this.selected) {
          this.$emit('input', this.selected.value)
          this.$emit('inputChoices', this.selected.text)
          this.$emit('inputDetailChoice', this.selected)
        } else {
          this.$emit('input', null)
        }
      } else if (this.inputType === 'multi-select') {
        if (this.selected.length) {
          this.$emit(
            'input',
            this.selected.map((item) => (typeof item === 'string' && this.taggable ? item : item.value)),
          )
          this.$emit('inputChoices', this.selected)
        } else {
          this.$emit('input', [])
          this.$emit('inputChoices', [])
        }
      }
    },

    async value(newValue, oldValue) {
      if (this.inputType === 'model-select') {
        if (!newValue) {
          await this.searchRessource('')
          this.selected = null
        } else if (!oldValue && !this.selected && this.init) {
          await this.initField()
        }
      } else if (this.inputType === 'multi-select') {
        if (!newValue?.length && this.selected?.length) {
          await this.searchRessource('')
          this.selected = []
        } else if (
          ((!oldValue?.length && !this.selected?.length) || oldValue.join() !== newValue.join()) &&
          this.init
        ) {
          await this.initField()
        }
      }
      this.fieldKey += 1
    },

    async additionalRequest() {
      await this.searchRessource('')
    },

    async limitTo() {
      await this.searchRessource('')
    },
  },

  async mounted() {
    await this.initField()
  },

  methods: {
    async initField() {
      await this.searchRessource('')
      if (this.init) {
        if (this.inputType === 'multi-select' && this.value?.length) {
          try {
            const [ressources] = await APIRequest.get_with_request(
              this.baseUrl,
              this.ressource,
              `?${this.valueChoice}__in=${this.value.join()}&page_size=100`,
              { 'x-toast-off': 'true' },
            )

            this.selected = ressources.map((ressource) => ({
              text: this.textChoices.map((textChoice) => ressource[textChoice]).join(' '),
              value: ressource[this.valueChoice],
              object: {
                [this.valueChoice]: ressource[this.valueChoice],
                ...Object.fromEntries(
                  Object.entries(ressource).filter(
                    ([key]) => this.textChoices.includes(key) || this.additionalFields.includes(key),
                  ),
                ),
              },
            }))
          } catch (e) {
            this.$emit('fieldNotFound')
          }
        } else if (this.inputType === 'model-select' && this.value) {
          try {
            const ressource = await APIRequest.get_by_id(this.baseUrl, this.ressource, this.value, {
              'x-toast-off': 'true',
            })
            this.selected = {
              text: this.textChoices.map((textChoice) => ressource[textChoice]).join(' '),
              value: ressource[this.valueChoice],
              object: ressource,
            }
          } catch (e) {
            this.$emit('fieldNotFound')
          }
        }
      } else if (this.inputType === 'model-select') {
        this.selected = this.value
      } else if (this.inputType === 'multi-select') {
        this.selected = [...this.value]
      }

      this.fieldKey += 1
    },

    async searchRessource(input) {
      const [ressources] = await APIRequest.get_with_request(
        this.baseUrl,
        this.ressource,
        `?search=${input}&fields=${this.textChoices.join(',')},${this.additionalFields},${this.valueChoice},&${
          this.valueChoice
        }__in=${this.limitTo.join()}${this.additionalRequest}`,
        {
          'x-toast-off': 'true',
        },
      )
      this.choices = ressources.map((ressource) => ({
        text: this.textChoices.map((textChoice) => ressource[textChoice]).join(' '),
        value: ressource[this.valueChoice],
        object: ressource,
      }))
    },

    async createNewOption() {
      if (
        this.inputType === 'model-select' &&
        this.selected &&
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(this.selected.value) // UUID regex
      ) {
        const newItem = {}
        newItem[this.textChoices[0]] = this.selected.value
        if (this.selected.value) {
          const response = await APIRequest.create(this.baseUrl, this.ressource, newItem)
          const itemObject = {
            text: this.textChoices.map((textChoice) => response[textChoice]).join(' '),
            value: response[this.valueChoice],
          }
          this.choices.push(itemObject)
          this.selected = itemObject
        }
      } else if (this.inputType === 'multi-select' && this.selected?.length) {
        const needCreate = this.selected.filter((value) => typeof value === 'string')
        const createList = []
        needCreate.forEach((item) => {
          const newItem = {}
          newItem[this.textChoices[0]] = item
          createList.push(newItem)
        })
        if (createList.length) {
          const response = await APIRequest.create(this.baseUrl, this.ressource, createList)
          response.forEach((item) => {
            const itemText = this.textChoices.map((textChoice) => item[textChoice]).join(' ')
            const itemIndex = this.selected.findIndex((selectedItem) => selectedItem === itemText)
            const itemObject = {
              text: itemText,
              value: item[this.valueChoice],
            }
            this.selected[itemIndex] = itemObject
            this.choices.push(itemObject)
          })
          this.$emit(
            'input',
            this.selected.map((item) => item.value),
          )
        }
      }
    },
  },
}
</script>

<style scoped></style>
